import React, { useState, useEffect, useRef } from 'react';
import { useStripe, useElements, CardNumberElement } from '@stripe/react-stripe-js';
import { withStyles, Button, TextField, Grid, CircularProgress } from '@material-ui/core';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { showNotification } from 'react-admin';
import CardSection from './CardSection';
import { clientSecretSelector } from '../../../core/redux/stripe/selectors';

const styles = theme => ({
  container: {
    backgroundColor: '#fbfbfb',
    border: '1px solid #d6d6d6',
    borderRadius: 4,
    padding: theme.spacing.unit * 4,
  },
  containerGrid: {
    marginBottom: theme.spacing.unit * 2,
  },
  buttonSubmit: {
    marginTop: theme.spacing.unit * 2,
  },
  bootstrapRoot: {
    padding: 0,
    'label + &': {
      marginTop: theme.spacing.unit * 3,
    },
  },
  bootstrapInput: {
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
    border: '1px solid #d2dae1',
    fontSize: 16,
    padding: '10px 12px',
    width: 'calc(100% - 24px)',
    fontFamily: ['Roboto', 'Helvetica', 'Arial', 'sans-serif'].join(','),
    '&::placeholder': {
      color: '#a6a6a6',
      opacity: 1,
    },
    '&:focus': {
      borderColor: '#969696',
    },
  },
  bootstrapFormLabel: {
    fontSize: 16,
    transform: 'translate(0, 1.5px) scale(1)',
  },
});

const CardSetupForm = ({
  isPostalCodeMandatory,
  classes,
  translate,
  handleCloseDialog,
  dispatch,
  setMopUpdating,
  clientSecret,
  synchronizeMOP,
  resetStripeFlow,
}) => {
  const stripeRef = useRef(null);
  const elementsRef = useRef(null);

  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (stripe) stripeRef.current = stripe;
    if (elements) elementsRef.current = elements;
  }, [stripe, elements]);

  const card = {
    number: false,
    expiryDate: false,
    cvc: false,
  };

  const [isCardReady, setIsCardReady] = useState(card);
  const [cardHolderName, setCardHolderName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [postalCode, setPostalCode] = useState('');

  const onCardChange = (event, type) => {
    switch (type) {
      case 'number':
      case 'expiryDate':
      case 'cvc':
        if (event.complete) {
          setIsCardReady({ ...isCardReady, [type]: true });
        } else if (event.error) {
          setIsCardReady({ ...isCardReady, [type]: false });
        }
        break;

      default:
        break;
    }
  };

  const isCardCompleted = () => {
    // all card values are true
    const areAllCardValuesTrue = Object.values(isCardReady).every(val => val);
    const isNum = val => /^\d+$/.test(val);
    if (isPostalCodeMandatory) {
      return areAllCardValuesTrue && isNum(postalCode) && postalCode.length === 5 && cardHolderName;
    }
    return areAllCardValuesTrue && cardHolderName;
  };

  const confirmSetup = async () => {
    if (!clientSecret || !stripeRef.current || !elementsRef.current) {
      console.error('Missing required values for Stripe confirmation');
      return;
    }
    try {
      const cardRef = elementsRef.current.getElement(CardNumberElement);
      const result = await stripeRef.current.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardRef,
          billing_details: {
            name: cardHolderName,
            address: {
              postal_code: postalCode,
            },
          },
        },
      });

      if (result.error) {
        dispatch(showNotification(result.error && result.error.code, 'error'));
        console.error('Stripe error:', result.error);
      } else {
        dispatch(showNotification(translate('stripe.paymentSuccess'), 'success'));
        synchronizeMOP();
        setMopUpdating(true);
        resetStripeFlow();
        handleCloseDialog();
      }
    } catch (error) {
      console.error('Exception in Stripe call:', error);
      dispatch(showNotification('Error processing payment', 'error'));
    }
  };

  const handleSubmit = event => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    setIsLoading(true);
    confirmSetup();
  };

  if (!stripe || !elements) {
    return <CircularProgress />;
  }

  return (
    <>
      <div className={classes.container}>
        <div className={classes.containerGrid}>
          <Grid container item spacing={16} className={classes.containerGrid}>
            <Grid item xs={12} sm={8} md={6}>
              <TextField
                fullWidth
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    root: classes.bootstrapRoot,
                    input: classes.bootstrapInput,
                  },
                }}
                InputLabelProps={{
                  shrink: true,
                  focused: false,
                  className: classes.bootstrapFormLabel,
                }}
                name="cardHolderName"
                id="cardHolderName"
                label={translate('stripe.cardholderName')}
                placeholder={translate('stripe.cardholderNamePlaceholder')}
                defaultValue=""
                onChange={e => setCardHolderName(e.target.value)}
              />
            </Grid>
          </Grid>
          <CardSection onCardChange={onCardChange} classes={classes} translate={translate} />
          {isPostalCodeMandatory && (
            <Grid container item spacing={16}>
              <Grid item xs={6} sm={4} md={3}>
                <TextField
                  fullWidth
                  InputProps={{
                    disableUnderline: true,
                    classes: {
                      root: classes.bootstrapRoot,
                      input: classes.bootstrapInput,
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                    focused: false,
                    className: classes.bootstrapFormLabel,
                  }}
                  name="zipCode"
                  id="zipCode"
                  label={translate('stripe.zipCode')}
                  placeholder="12345"
                  defaultValue=""
                  onChange={e => setPostalCode(e.target.value)}
                />
              </Grid>
            </Grid>
          )}
        </div>
      </div>

      <Button
        className={classes.buttonSubmit}
        onClick={handleSubmit}
        fullWidth
        variant="contained"
        color="primary"
        disabled={!stripe || !isCardCompleted() || isLoading}
      >
        {translate('stripe.saveCard')}
      </Button>
    </>
  );
};

CardSetupForm.propTypes = {
  isPostalCodeMandatory: PropTypes.bool,
  translate: PropTypes.func,
  classes: PropTypes.shape({}),
  dispatch: PropTypes.func,
  handleCloseDialog: PropTypes.func.isRequired,
  setMopUpdating: PropTypes.func.isRequired,
  synchronizeMOP: PropTypes.func.isRequired,
  resetStripeFlow: PropTypes.func.isRequired,
  clientSecret: PropTypes.string,
};

const mapStateToProps = state => ({
  clientSecret: clientSecretSelector(state),
});

const enhance = compose(
  connect(mapStateToProps),
  withStyles(styles)
);

export default enhance(CardSetupForm);
